/**
 * Workers Marketplace API
 * Client for the 0.works Workers marketplace (Agent Environment Packages)
 */

import { getApiClient } from './client.js';
import { createReadStream, statSync } from 'fs';
import { basename } from 'path';

export interface WorkerPackage {
  id: string;
  slug: string;
  publisherId: number;
  publisherName?: string;
  displayName: string;
  shortDescription: string;
  description: string;
  version: string;
  priceType: 'free' | 'credits' | 'usd';
  priceAmount: number;
  compatibleAgents: string[];
  categories: string[];
  digitalTwinHint?: string;
  downloadCount: number;
  avgRating: number;
  reviewCount: number;
  purchaseCount: number;
  status: string;
  reviewStatus: string;
  fileSize: number;
  fileHash: string;
  createdAt: string;
  updatedAt: string;
}

export interface WorkerUploadResult {
  success: boolean;
  worker: WorkerPackage;
  isVersionUpdate?: boolean;
}

export async function uploadWorkerPackage(
  zipPath: string,
  metadata: Record<string, unknown>
): Promise<WorkerUploadResult> {
  const client = getApiClient();
  const FormData = (await import('form-data')).default;
  const form = new FormData();
  const stat = statSync(zipPath);
  form.append('file', createReadStream(zipPath), {
    filename: basename(zipPath),
    contentType: 'application/zip',
    knownLength: stat.size,
  });
  form.append('metadata', JSON.stringify(metadata));

  const { data } = await client.post<WorkerUploadResult>('/workers', form, {
    headers: form.getHeaders(),
    maxContentLength: 500 * 1024 * 1024,
    maxBodyLength: 500 * 1024 * 1024,
  });
  return data;
}

export async function publishWorkerPackage(
  id: string
): Promise<{ success: boolean; worker: WorkerPackage }> {
  const client = getApiClient();
  const { data } = await client.post<{ success: boolean; worker: WorkerPackage }>(
    `/workers/${id}/publish`
  );
  return data;
}

export async function updateWorkerPackage(
  id: string,
  body: Record<string, unknown>
): Promise<{ success: boolean; worker: WorkerPackage }> {
  const client = getApiClient();
  const { data } = await client.put<{ success: boolean; worker: WorkerPackage }>(
    `/workers/${id}`,
    body
  );
  return data;
}

export async function getWorkerBySlug(
  slug: string
): Promise<{ success: boolean; worker: WorkerPackage }> {
  const client = getApiClient();
  const { data } = await client.get<{ success: boolean; worker: WorkerPackage }>(
    `/workers/${slug}`
  );
  return data;
}

export async function listMyPublishedWorkers(
  params?: Record<string, string>
): Promise<{ success: boolean; items: WorkerPackage[]; total: number }> {
  const client = getApiClient();
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  const { data } = await client.get<{ success: boolean; items: WorkerPackage[]; total: number }>(
    `/workers/my/published${qs}`
  );
  return data;
}

export async function listWorkerPackages(
  params?: Record<string, string>
): Promise<{ success: boolean; items: WorkerPackage[]; total: number }> {
  const client = getApiClient();
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  const { data } = await client.get<{ success: boolean; items: WorkerPackage[]; total: number }>(
    `/workers${qs}`
  );
  return data;
}

export async function deleteWorkerPackage(
  id: string
): Promise<{ success: boolean; worker: WorkerPackage }> {
  const client = getApiClient();
  const { data } = await client.delete<{ success: boolean; worker: WorkerPackage }>(
    `/workers/${id}`
  );
  return data;
}

export interface WorkerVersionItem {
  id: string;
  workerId: string;
  version: string;
  gcsKey: string;
  fileSize: number;
  fileHash: string;
  changelog?: string | null;
  createdAt: string;
}

export async function listWorkerVersions(
  slug: string,
  params?: Record<string, string>
): Promise<{ success: boolean; items: WorkerVersionItem[]; total: number; currentVersion: string }> {
  const client = getApiClient();
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  const { data } = await client.get<{ success: boolean; items: WorkerVersionItem[]; total: number; currentVersion: string }>(
    `/workers/${slug}/versions${qs}`
  );
  return data;
}

// ── Consumer APIs ────────────────────────────────────────────────────

export interface WorkerPurchase {
  id: string;
  workerId: string;
  userId: number;
  slug?: string;
  displayName?: string;
  version?: string;
  priceType?: string;
  pricePaid?: number;
  createdAt: string;
  worker?: WorkerPackage;
}

export async function purchaseWorker(
  slug: string
): Promise<{ success: boolean; purchase: WorkerPurchase }> {
  const client = getApiClient();
  const { data } = await client.post<{ success: boolean; purchase: WorkerPurchase }>(
    `/workers/${slug}/purchase`
  );
  return data;
}

export async function downloadWorker(
  slug: string,
  version?: string
): Promise<{ success: boolean; downloadUrl: string; worker: WorkerPackage }> {
  const client = getApiClient();
  const qs = version ? `?version=${encodeURIComponent(version)}` : '';
  const { data } = await client.get<{ success: boolean; downloadUrl: string; worker: WorkerPackage }>(
    `/workers/${slug}/download${qs}`
  );
  return data;
}

export async function listMyPurchasedWorkers(
  params?: Record<string, string>
): Promise<{ success: boolean; items: WorkerPurchase[]; total: number }> {
  const client = getApiClient();
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  const { data } = await client.get<{ success: boolean; items: WorkerPurchase[]; total: number }>(
    `/workers/my/purchased${qs}`
  );
  return data;
}

export async function getUserProfile(): Promise<{ success: boolean; user: { id: number; email: string; displayName?: string; tier?: string; credits?: number } }> {
  const client = getApiClient();
  const { data } = await client.get<{ success: boolean; user: { id: number; email: string; displayName?: string; tier?: string; credits?: number } }>(
    '/user/profile'
  );
  return data;
}
